import React, { useEffect, useState } from "react";
import {
  getContentTopicsList,
  deleteContentTopic,
} from "../../../services/contentTopicsService";
import { paginateData, calculateTotalPages } from "../../../assets/js/script";
import TableHeader from "../../Common/TableComponent/TableHeader";
import TableToolbar from "../../Common/TableComponent/TableToolbar";
import TablesRow from "../../Common/TableComponent/TablesRow";
import { renderStatusBadge } from "../../Common/TableComponent/tableBadges";
import { Pagination } from "../../Common/TableComponent/Pagination";
import { AddContentTopic } from "./AddContentTopic";
import { Loading } from "../../Common/OtherElements/Loading";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { confirmDelete } from "../../Common/OtherElements/confirmDeleteClone";
import { TableDataStatusError } from "../../Common/OtherElements/TableDataStatusError";
import { handleErrors } from "../../../utils/errorHandler";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";

const getTopicId = (item) => item.id ?? item.Id;

const getTopicName = (item) => item.topicName || item.TopicName || "-";

export const ManageContentTopicsContent = () => {
  const [pageAccessDetails, setPageAccessDetails] = useState({});
  const pageLevelAccessUrl = "content-topics";
  const navigate = useNavigate();
  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [topicsList, setTopicsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess && !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getContentTopicsList();
      const list = response.data?.result || response.data || [];
      setTopicsList(Array.isArray(list) ? list : []);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = topicsList.filter((item) => {
    const name = getTopicName(item).toLowerCase();
    const query = searchQuery.toLowerCase();
    return name.includes(query);
  });

  const currentData = paginateData(filteredData, currentPage, entriesPerPage);
  const totalPages = calculateTotalPages(filteredData.length, entriesPerPage);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDelete = async (topicId) => {
    const confirmed = await confirmDelete("Tag");
    if (confirmed) {
      try {
        await deleteContentTopic(topicId);
        setTopicsList((prev) =>
          prev.filter((item) => getTopicId(item) !== topicId)
        );
        Swal.fire(
          "Deleted!",
          "The tag has been deleted successfully.",
          "success"
        );
        fetchData();
      } catch (error) {
        handleErrors(error);
      }
    }
  };

  return (
    <>
      {pageAccessDetails.addAccess ? (
        <AddContentTopic
          editMode={editMode}
          initialData={selectedTopic ?? {}}
          onSuccess={fetchData}
          setSelectedTopic={setSelectedTopic}
          setEditMode={setEditMode}
          topicsCount={topicsList.length}
        />
      ) : (
        ""
      )}

      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5">
              <div className="card-header">
                <h5 className="mb-sm-2 mt-sm-2">Manage Tags</h5>
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <TableToolbar
                  entriesPerPage={entriesPerPage}
                  onEntriesChange={handleEntriesChange}
                  searchValue={searchQuery}
                  searchPlaceholder="Search..."
                  onSearchChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                  }}/>

                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader
                        columns={["Tag Name", "Status", "Action"]}
                      />
                      <tbody className="manage-page-group-table-values">
                        {currentData.length === 0 ? (
                          <TableDataStatusError colspan="3" />
                        ) : (
                          currentData.map((item) => (
                            <TablesRow
                              key={getTopicId(item)}
                              rowData={{
                                tagName: getTopicName(item),
                                status: renderStatusBadge(item),
                              }}
                              columns={["tagName", "status"]}
                              hideIcons={false}
                              onEdit={() => {
                                setSelectedTopic(item);
                                setEditMode(true);
                                window.scrollTo({ top: 0, behavior: "smooth" });
                              }}
                              onDelete={() => handleDelete(getTopicId(item))}
                              pageLevelAccessData={pageAccessDetails}
                            />
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={filteredData.length}
                  entriesPerPage={entriesPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
};
